(() => {
  const GIFT_PROPERTY = '_kuuraii_reward_gift';
  const TIER_PROPERTY = '_kuuraii_reward_tier';

  let syncTimer;
  let activeSync = null;
  let checkoutPreflightRunning = false;
  let replayingCheckout = false;

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  function getConfig() {
    const drawer = getDrawer();
    if (!drawer) return null;

    return {
      drawer,
      enabled:
        drawer.dataset.indiaPromotionsEnabled === 'true' &&
        drawer.dataset.rewardAutoGifts === 'true',
      tiers: [
        {
          key: 'one',
          threshold: Number(drawer.dataset.rewardGiftOneThreshold || 0),
          variantId: Number(drawer.dataset.rewardGiftOneVariant || 0),
          label: drawer.dataset.rewardGiftOneLabel || 'Checked towel',
        },
        {
          key: 'two',
          threshold: Number(drawer.dataset.rewardGiftTwoThreshold || 0),
          variantId: Number(drawer.dataset.rewardGiftTwoVariant || 0),
          label: drawer.dataset.rewardGiftTwoLabel || 'Grid towel',
        },
        {
          key: 'three',
          threshold: Number(drawer.dataset.rewardGiftThreeThreshold || 0),
          variantId: Number(drawer.dataset.rewardGiftThreeVariant || 0),
          label: drawer.dataset.rewardGiftThreeLabel || 'Diamond towel',
        },
        {
          key: 'four',
          threshold: Number(drawer.dataset.rewardGiftFourThreshold || 0),
          variantId: Number(drawer.dataset.rewardGiftFourVariant || 0),
          label: drawer.dataset.rewardGiftFourLabel || 'Bamboo towel',
        },
      ].filter((tier) => tier.threshold > 0 && tier.variantId > 0),
    };
  }

  function isRewardGift(item) {
    return Boolean(item && item.properties && String(item.properties[GIFT_PROPERTY]) === 'true');
  }

  function isKnownGiftVariant(item, tiers) {
    const variantId = Number(item && item.variant_id);
    return tiers.some((tier) => tier.variantId === variantId);
  }

  function qualifyingSubtotal(cart, tiers) {
    return (cart.items || []).reduce((total, item) => {
      // Exclude every known gift variant, even when someone manually added it
      // without the private reward property.
      if (isRewardGift(item) || isKnownGiftVariant(item, tiers)) return total;
      return total + Number(item.original_line_price || item.final_line_price || 0);
    }, 0);
  }

  function desiredTier(cart, tiers) {
    const subtotal = qualifyingSubtotal(cart, tiers);
    return tiers.reduce((match, tier) => {
      return subtotal >= tier.threshold ? tier : match;
    }, null);
  }

  function requestJson(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }).then((response) => {
      if (!response.ok) throw new Error(`Cart request failed with ${response.status}`);
      return response.json();
    });
  }

  function fetchCart() {
    return fetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }).then((response) => {
      if (!response.ok) throw new Error('Cart fetch failed');
      return response.json();
    });
  }

  function sectionRequest() {
    return {
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    };
  }

  function renderCart(response) {
    if (!response || !response.sections) return;
    const drawer = getDrawer();
    if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents(response);
  }

  function showCartMessage(message) {
    const error = document.getElementById('CartDrawer-CartErrors');
    if (error) error.textContent = message || '';
  }

  function productHandle(item) {
    if (item && item.handle) return String(item.handle);
    const match = item && item.url && String(item.url).match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  async function unavailableCartItems(cart) {
    const items = (cart && cart.items) || [];
    const productCache = new Map();

    const checks = await Promise.all(
      items.map(async (item) => {
        const handle = productHandle(item);
        if (!handle) return null;

        try {
          if (!productCache.has(handle)) {
            productCache.set(
              handle,
              fetch(`${window.Shopify.routes.root}products/${encodeURIComponent(handle)}.js`, {
                headers: { Accept: 'application/json' },
                cache: 'no-store',
              }).then((response) => (response.ok ? response.json() : null))
            );
          }

          const product = await productCache.get(handle);
          const variant = product &&
            (product.variants || []).find((candidate) => Number(candidate.id) === Number(item.variant_id));
          return variant && variant.available ? null : item;
        } catch (error) {
          // A temporary network failure should not remove a valid line.
          return null;
        }
      })
    );

    return checks.filter(Boolean);
  }

  async function removeItems(items) {
    const updates = {};
    items.forEach((item) => {
      if (item && item.key) updates[item.key] = 0;
    });

    if (!Object.keys(updates).length) return null;

    return requestJson(`${window.Shopify.routes.root}cart/update.js`, {
      updates,
      ...sectionRequest(),
    });
  }

  async function performRewardSync() {
    const config = getConfig();
    if (!config) return true;

    let cart = await fetchCart();

    // A cart survives a Shopify Market switch. Remove an India-only reward
    // before an international customer can continue to Paytring checkout.
    if (!config.enabled) {
      const ineligibleGifts = (cart.items || []).filter((item) => {
        return isRewardGift(item) || isKnownGiftVariant(item, config.tiers);
      });
      const response = await removeItems(ineligibleGifts);
      renderCart(response);
      return true;
    }

    if (!config.tiers.length) return true;

    const desired = desiredTier(cart, config.tiers);
    let latestResponse = null;

    const validDesiredLine = desired
      ? cart.items.find(
          (item) =>
            isRewardGift(item) &&
            isKnownGiftVariant(item, config.tiers) &&
            Number(item.variant_id) === desired.variantId
        )
      : null;

    if (desired && !validDesiredLine) {
      latestResponse = await requestJson(`${window.Shopify.routes.root}cart/add.js`, {
        items: [
          {
            id: desired.variantId,
            quantity: 1,
            properties: {
              [GIFT_PROPERTY]: 'true',
              [TIER_PROPERTY]: desired.key,
              _kuuraii_reward_label: desired.label,
            },
          },
        ],
        ...sectionRequest(),
      });
      cart = await fetchCart();
    }

    const updates = {};
    let keptDesiredGift = false;

    (cart.items || []).forEach((item) => {
      const knownGiftVariant = isKnownGiftVariant(item, config.tiers);
      const rewardMarked = isRewardGift(item);

      // Remove old reward lines and manually-added zero-price gift variants.
      if (!knownGiftVariant && !rewardMarked) return;

      const matchesDesired = Boolean(
        desired &&
          knownGiftVariant &&
          rewardMarked &&
          Number(item.variant_id) === desired.variantId
      );

      if (matchesDesired && !keptDesiredGift) {
        keptDesiredGift = true;
        if (Number(item.quantity) !== 1) updates[item.key] = 1;
        return;
      }

      updates[item.key] = 0;
    });

    if (Object.keys(updates).length) {
      latestResponse = await requestJson(`${window.Shopify.routes.root}cart/update.js`, {
        updates,
        ...sectionRequest(),
      });
    }

    renderCart(latestResponse);
    return true;
  }

  function syncRewards() {
    if (activeSync) return activeSync;

    activeSync = performRewardSync()
      .catch((error) => {
        console.error('Kuuraii cart rewards:', error);
        showCartMessage('The complimentary gift could not be updated. Please refresh your cart.');
        return false;
      })
      .finally(() => {
        activeSync = null;
      });

    return activeSync;
  }

  function scheduleSync(delay = 180) {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncRewards, delay);
  }

  async function checkoutPreflight(event) {
    const checkout = event.target && event.target.closest
      ? event.target.closest('#CartDrawer-Checkout, [data-paytring-checkout]')
      : null;

    if (!checkout || replayingCheckout || checkoutPreflightRunning || checkout.disabled) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutPreflightRunning = true;
    checkout.setAttribute('aria-busy', 'true');
    checkout.disabled = true;
    showCartMessage('');

    try {
      const rewardsReady = await syncRewards();
      if (!rewardsReady) throw new Error('Reward sync failed');

      const cart = await fetchCart();
      const unavailable = await unavailableCartItems(cart);

      if (unavailable.length) {
        const response = await removeItems(unavailable);
        renderCart(response);

        const names = [
          ...new Set(unavailable.map((item) => item.product_title || item.title).filter(Boolean)),
        ];
        const itemWord = names.length === 1 ? 'item is' : 'items are';
        showCartMessage(
          `${names.join(', ')} ${itemWord} no longer available and ${
            names.length === 1 ? 'has' : 'have'
          } been removed. Please proceed to checkout again.`
        );
        scheduleSync(0);
        return;
      }

      const currentCheckout = document.getElementById(checkout.id);
      if (!currentCheckout) throw new Error('Checkout button was not found');

      replayingCheckout = true;
      currentCheckout.disabled = false;
      currentCheckout.removeAttribute('aria-busy');
      currentCheckout.click();
      replayingCheckout = false;
    } catch (error) {
      console.error('Kuuraii checkout preflight:', error);
      showCartMessage('We could not prepare your cart for checkout. Please refresh and try again.');
    } finally {
      checkoutPreflightRunning = false;
      const currentCheckout = document.getElementById(checkout.id);
      if (currentCheckout) {
        currentCheckout.disabled = false;
        currentCheckout.removeAttribute('aria-busy');
      }
      replayingCheckout = false;
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    const drawer = getDrawer();
    if (!drawer) return;

    // Sanitize zero-price gift variants immediately, including direct cart links.
    scheduleSync(0);

    const cartTrigger = document.getElementById('cart-icon-bubble');
    if (cartTrigger) cartTrigger.addEventListener('click', () => scheduleSync(0));

    document.addEventListener('click', checkoutPreflight, true);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleSync(0));
    }

    const observer = new MutationObserver(() => {
      if (!activeSync && drawer.classList.contains('active')) scheduleSync();
    });
    observer.observe(drawer, { childList: true, subtree: true });

    window.addEventListener('pageshow', () => scheduleSync(0));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') scheduleSync(0);
    });
  });
})();
